import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import UserProfileModal from './UserProfileModal';

function getRoleDisplayName(role) {
  const names = {
    sysadmin: 'System Administrator',
    admin: 'Administrator',
    client: 'Client',
    contractor: 'Contractor',
    dre: 'DRE',
  };
  return names[role?.toLowerCase()] || role;
}

function Sidebar({ isOpen, toggleSidebar, isCollapsed, toggleCollapsed, onLogout, userRole, screens = [] }) {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [wayleaveQueueCount, setWayleaveQueueCount] = useState(0);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  // Poll the Wayleave "My Actions" count for the nav badge (users without
  // Documents access get a 403 → badge stays hidden)
  useEffect(() => {
    if (!screens.includes('wayleave')) return undefined;
    let active = true;
    const load = () => axios.get('/api/wayleave-docs/queue', { withCredentials: true })
      .then((res) => { if (active) setWayleaveQueueCount((res.data || []).length); })
      .catch(() => { if (active) setWayleaveQueueCount(0); });
    load();
    const t = setInterval(load, 60000);
    return () => { active = false; clearInterval(t); };
  }, [screens]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('/api/user/profile', { withCredentials: true });
      setCurrentUser(response.data);
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    }
  };

  const handleProfileClick = () => {
    setShowProfileModal(true);
  };

  // Menu items with role-based visibility
  const menuItems = [
    {
      to: '/',
      end: true,
      screen: 'dashboard',
      label: 'Dashboard',
      icon: (
        <svg className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2.5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    },
    {
      to: '/station-info',
      screen: 'station-info',
      label: 'Station Info',
      icon: (
        <svg className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2.5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      to: '/location',
      screen: 'location',
      label: 'Damaged Stations',
      icon: (
        <svg className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2.5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
    {
      to: '/wayleave',
      screen: 'wayleave',
      label: 'Wayleaves',
      icon: (
        <svg className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2.5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      to: '/reports',
      screen: 'reports',
      label: 'Reports',
      icon: (
        <svg className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2.5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
  ];

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Sidebar - now always fixed */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 bg-white shadow-lg transform transition-all duration-300 ease-in-out ${
          isCollapsed ? 'w-14' : 'w-56'
        } ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo with collapse button */}
          <div className={`border-b border-gray-200 relative ${isCollapsed ? 'p-2.5' : 'p-5'}`}>
            {!isCollapsed ? (
              <>
                <img src="/logo.png" alt="TCS Insight" className="h-14 w-auto mb-2 mx-auto" />
                <button
                  onClick={toggleCollapsed}
                  className="hidden lg:block absolute bottom-2 right-2 p-1.5 hover:bg-gray-100 rounded-lg"
                  title="Collapse sidebar"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              </>
            ) : (
              <button
                onClick={toggleCollapsed}
                className="hidden lg:block p-2 hover:bg-gray-100 rounded-lg w-full"
                title="Expand sidebar"
              >
                <svg className="w-4 h-4 mx-auto text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className={`flex-1 flex flex-col overflow-y-auto ${isCollapsed ? 'p-1.5' : 'p-3'}`}>
            <div className="space-y-1.5">
              {menuItems.filter(item => screens.includes(item.screen)).map((item) => {
                const badge = item.screen === 'wayleave' && wayleaveQueueCount > 0 ? wayleaveQueueCount : 0;
                return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `relative flex items-center rounded-lg transition-colors text-sm ${
                      isCollapsed ? 'justify-center px-1.5 py-2.5' : 'px-3 py-2.5'
                    } ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                  title={isCollapsed ? `${item.label}${badge ? ` (${badge})` : ''}` : ''}
                >
                  {item.icon}
                  {!isCollapsed && item.label}
                  {badge > 0 && (
                    isCollapsed ? (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />
                    ) : (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-amber-500 rounded-full">
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )
                  )}
                </NavLink>
                );
              })}
            </div>

            {/* Bottom nav items */}
            <div className="mt-auto pt-2 space-y-1.5">
              {/* Settings - sysadmin only */}
              {screens.includes('settings') && (
                <NavLink
                  to="/settings"
                  className={({ isActive }) =>
                    `flex items-center rounded-lg transition-colors text-sm ${
                      isCollapsed ? 'justify-center px-1.5 py-2.5' : 'px-3 py-2.5'
                    } ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                  title={isCollapsed ? 'Settings' : ''}
                >
                  <svg className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2.5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {!isCollapsed && 'Settings'}
                </NavLink>
              )}

              {/* Help & Feedback - all roles */}
              <NavLink
                to="/help"
                className={({ isActive }) =>
                  `flex items-center rounded-lg transition-colors text-sm ${
                    isCollapsed ? 'justify-center px-1.5 py-2.5' : 'px-3 py-2.5'
                  } ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
                title={isCollapsed ? 'Help & Feedback' : ''}
              >
                <svg className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2.5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {!isCollapsed && 'Help & Feedback'}
              </NavLink>
            </div>
          </nav>

          {/* User info at bottom */}
          {!isCollapsed && (
            <div className="p-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div
                  className="flex items-center flex-1 cursor-pointer hover:bg-gray-50 rounded-lg p-1.5 -ml-1.5 transition-colors"
                  onClick={handleProfileClick}
                  title="Click to edit profile"
                >
                  {currentUser?.photoUrl ? (
                    <img
                      src={currentUser.photoUrl}
                      alt="Profile"
                      className="w-9 h-9 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold text-sm">
                      {currentUser?.firstName?.[0] || currentUser?.username?.[0] || 'U'}
                    </div>
                  )}
                  <div className="ml-2.5">
                    <p className="text-xs font-medium text-gray-900">
                      {currentUser?.firstName && currentUser?.lastName
                        ? `${currentUser.firstName} ${currentUser.lastName}`
                        : currentUser?.username || 'User'}
                    </p>
                    <p className="text-xs text-gray-500">{getRoleDisplayName(userRole)}</p>
                  </div>
                </div>
                <button
                  onClick={onLogout}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Logout"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="p-1.5 border-t border-gray-200 flex flex-col items-center space-y-1.5">
              <div
                className="cursor-pointer hover:opacity-80 transition-opacity"
                onClick={handleProfileClick}
                title="Click to edit profile"
              >
                {currentUser?.photoUrl ? (
                  <img
                    src={currentUser.photoUrl}
                    alt="Profile"
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold text-sm">
                    {currentUser?.firstName?.[0] || currentUser?.username?.[0] || 'U'}
                  </div>
                )}
              </div>
              <button
                onClick={onLogout}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="Logout"
              >
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        currentUser={currentUser}
      />
    </>
  );
}

export default Sidebar;
